import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';

import type {
  DaySchedule,
  OpenInterval,
  WorkingHours,
} from '@/integrations/algolia/types';
dayjs.extend(isBetween);
dayjs.extend(utc);

const MILLISECONDS_IN_A_DAY = 86_400_000;
const MILLISECONDS_IN_AN_HOUR = 3_600_000;
const MILLISECONDS_IN_A_MINUTE = 60_000;
const MILLISECONDS_IN_A_SECOND = 1000;

export const isTimestampInThePast = (timestamp: number) =>
  timestamp * 1000 - new Date().getTime() < 0;

export const getTimestampExpirationCountdown = (timestamp: number) => {
  const timeDifference = timestamp * 1000 - new Date().getTime();
  if (timeDifference > 0) {
    const days = Math.floor(timeDifference / MILLISECONDS_IN_A_DAY);

    const hours = Math.floor(
      (timeDifference - days * MILLISECONDS_IN_A_DAY) / MILLISECONDS_IN_AN_HOUR,
    );

    const minutes = Math.floor(
      (timeDifference -
        days * MILLISECONDS_IN_A_DAY -
        hours * MILLISECONDS_IN_AN_HOUR) /
        MILLISECONDS_IN_A_MINUTE,
    );

    const seconds = Math.floor(
      (timeDifference -
        days * MILLISECONDS_IN_A_DAY -
        hours * MILLISECONDS_IN_AN_HOUR -
        minutes * MILLISECONDS_IN_A_MINUTE) /
        MILLISECONDS_IN_A_SECOND,
    );

    return {
      days,
      hours,
      minutes,
      seconds,
    };
  } else {
    return {
      days: -1,
      hours: -1,
      minutes: -1,
      seconds: -1,
    };
  }
};

export const padStart = (time: number) => String(time).padStart(2, '0');

export const dateIsBetween = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) return false;
  return dayjs
    .utc()
    .isBetween(
      dayjs(startDate).utc(true).format(),
      dayjs(endDate).utc(true).format(),
    );
};

export function calculateCenterHoursAndHolidays(hoursData: WorkingHours) {
  const weekdays = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  function formatTime24Hour(time24: string) {
    const [hours, minutes] = time24.split(':');
    return `${hours}:${minutes}`;
  }

  function formatDailyHours(openIntervals: OpenInterval[] | undefined) {
    if (openIntervals && openIntervals.length > 0) {
      return openIntervals
        .map(
          (interval) =>
            `${formatTime24Hour(interval.start)}-${formatTime24Hour(
              interval.end,
            )}`,
        )
        .join(', ');
    }
    return 'closed';
  }

  const weekdayHours = weekdays.map((day) => {
    const daySchedule = hoursData[day as keyof WorkingHours] as
      | DaySchedule
      | undefined;

    const holidayOverride = (hoursData.holidayHours || []).find((holiday) => {
      const holidayDate = new Date(holiday.date);
      const holidayDayName = weekdays[holidayDate.getDay() - 1];
      return holidayDayName === day;
    });

    if (holidayOverride) {
      return {
        day,
        hours: formatDailyHours(holidayOverride.openIntervals),
      };
    }

    return {
      day,
      hours: daySchedule?.isClosed
        ? 'closed'
        : formatDailyHours(daySchedule?.openIntervals),
    };
  });

  function groupConsecutiveDays(hoursList: { day: string; hours: string }[]) {
    const grouped: string[] = [];
    let currentGroup: { end: string; hours: string; start: string } | null =
      null;

    hoursList.forEach(({ day, hours }, index) => {
      if (!currentGroup || currentGroup.hours !== hours) {
        if (currentGroup) {
          grouped.push(
            currentGroup.start === currentGroup.end
              ? `${currentGroup.start}: ${currentGroup.hours}`
              : `${currentGroup.start}-${currentGroup.end}: ${currentGroup.hours}`,
          );
        }
        currentGroup = { start: day, end: day, hours };
      } else {
        currentGroup.end = day;
      }

      if (index === hoursList.length - 1 && currentGroup) {
        grouped.push(
          currentGroup.start === currentGroup.end
            ? `${currentGroup.start}: ${currentGroup.hours}`
            : `${currentGroup.start}-${currentGroup.end}: ${currentGroup.hours}`,
        );
      }
    });

    return grouped;
  }

  const weekdayHoursSubset = weekdayHours.slice(0, 5);
  const groupedWeekdayHours = groupConsecutiveDays(
    weekdayHoursSubset.map(({ day, hours }) => ({
      day: day.charAt(0).toUpperCase() + day.slice(1, 3),
      hours,
    })),
  );

  const displayHours = groupedWeekdayHours.join(', ');

  const weekendHours = weekdayHours.filter(
    ({ day }) => day === 'saturday' || day === 'sunday',
  );
  const formattedWeekendHours = weekendHours
    .sort((a, b) => weekdays.indexOf(a.day) - weekdays.indexOf(b.day))
    .map(
      ({ day, hours }) =>
        `${day.charAt(0).toUpperCase()}${day.slice(1)}: ${hours}`,
    )
    .join(', ');

  const upcomingHolidays = (hoursData.holidayHours || [])
    .filter((holiday) => {
      const holidayDate = new Date(holiday.date);
      const dayDiff =
        (holidayDate.getTime() - new Date().getTime()) / (1000 * 3600 * 24);
      return dayDiff >= -1 && dayDiff <= 15;
    })
    .map((holiday) => {
      const formattedDate = new Date(holiday.date).toLocaleDateString(
        undefined,
        {
          month: 'long',
          day: 'numeric',
        },
      );
      const holidayHours = formatDailyHours(holiday.openIntervals);
      return `${formattedDate}: ${holidayHours}`;
    });

  return {
    displayHours,
    upcomingHolidays,
    formattedWeekendHours,
  };
}

export const formatPublishDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const year = date.getFullYear();
  const monthName = date.toLocaleString('default', { month: 'long' });

  return `${day} ${monthName}, ${year}`;
};
